import React from 'react';
import { CheckIcon, UsersIcon, StarIcon, TicketIcon, MagnifyingGlassIcon, BuildingStorefrontIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const VenueSubscriptionStep = ({ 
    subscriptions, 
    selectedSubscription, 
    setSelectedSubscription,
    voucherCode,
    voucherValid,
    voucherLabel,
    calculateDiscountedPrice,
    validateVoucher,
    isPtDirectoryMode,
    setIsPtDirectoryMode
}) => {
    const [voucherInput, setVoucherInput] = React.useState(voucherCode || '');

    React.useEffect(() => {
        if (voucherCode) setVoucherInput(voucherCode);
    }, [voucherCode]);

    const handleApplyVoucher = () => {
        if (validateVoucher) validateVoucher(voucherInput);
    };

    // Split plans into the two groups
    const directoryPlans = subscriptions.filter(s => s.isPtDirectoryPlan === true);
    const venuePlans = subscriptions.filter(s => s.isPtDirectoryPlan !== true);

    // Which list to show based on active tab
    const visiblePlans = isPtDirectoryMode ? directoryPlans : venuePlans;

    // When switching tabs, clear any selection that belongs to the other group
    const handleTabSwitch = (toDirectory) => {
        if (!setIsPtDirectoryMode) return; // tab is locked, no-op
        setIsPtDirectoryMode(toDirectory);
        // If the currently selected plan belongs to the opposite group, clear it
        const currentPlan = subscriptions.find(s => s.id === selectedSubscription);
        if (currentPlan) {
            const currentIsDirectory = currentPlan.isPtDirectoryPlan === true;
            if (currentIsDirectory !== toDirectory) {
                setSelectedSubscription('');
            }
        }
    };

    // When setIsPtDirectoryMode is null the tab is locked (PT Directory signup)
    const tabLocked = !setIsPtDirectoryMode;

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-1">Choose Your Plan</h3>
                <p className="text-gray-400 text-sm">
                    Select the plan that best fits your needs. You can upgrade or downgrade at any time.
                </p>
            </div>

            {/* ── Tab switcher — hidden when tab is locked ── */}
            {!tabLocked && (
                <div className="flex justify-center">
                    <div className="inline-flex bg-gray-800 border border-gray-700 rounded-xl p-1 gap-1">
                        <button
                            type="button"
                            onClick={() => handleTabSwitch(true)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                isPtDirectoryMode
                                    ? 'bg-amber-500 text-gray-900 shadow'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                        >
                            <MagnifyingGlassIcon className="h-4 w-4" />
                            PT Directory
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTabSwitch(false)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                !isPtDirectoryMode
                                    ? 'bg-emerald-600 text-gray-900 shadow'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                            }`}
                        >
                            <BuildingStorefrontIcon className="h-4 w-4" />
                            Venue Plans
                        </button>
                    </div>
                </div>
            )}

            {/* Tab context hint */}
            <p className="text-center text-gray-500 text-xs -mt-2">
                {isPtDirectoryMode
                    ? 'Directory plans have lifetime pricing — your rate never increases.'
                    : 'All venue plans include a free PT Directory listing.'}
            </p>

            {/* ── Plan list ── */}
            <div className="grid gap-4">
                {visiblePlans.map((subscription) => {
                    const isDirectory = subscription.isPtDirectoryPlan === true;
                    const isFounder = subscription.isFounderPlan === true;
                    const originalPrice = subscription.price;
                    // Directory plans are never discounted by vouchers
                    const discountedPrice = (!isDirectory && calculateDiscountedPrice)
                        ? calculateDiscountedPrice(originalPrice)
                        : originalPrice;
                    const hasDiscount = voucherValid && !isDirectory && String(discountedPrice) !== String(originalPrice);
                    const isSelected = selectedSubscription === subscription.id;

                    return (
                        <label
                            key={subscription.id}
                            className={`relative flex cursor-pointer rounded-lg p-6 border-2 transition-all duration-200 ${
                                isSelected
                                    ? isFounder
                                        ? 'border-amber-500 bg-amber-500/10'
                                        : 'border-emerald-500 bg-emerald-500/10'
                                    : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                            }`}
                        >
                            <input
                                type="radio"
                                value={subscription.id}
                                checked={isSelected}
                                onChange={(e) => setSelectedSubscription(e.target.value)}
                                className="sr-only"
                            />

                            {/* Radio indicator */}
                            <div className={`flex-shrink-0 w-5 h-5 border-2 rounded-full flex items-center justify-center mr-4 mt-1 ${
                                isSelected
                                    ? isFounder ? 'border-amber-500 bg-amber-500' : 'border-emerald-500 bg-emerald-500'
                                    : 'border-gray-400'
                            }`}>
                                {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                            </div>

                            <div className="flex-1">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h4 className="text-xl font-semibold text-white flex items-center gap-2">
                                            {subscription.name}
                                            {isFounder && <StarIcon className="h-5 w-5 text-amber-400" />}
                                        </h4>

                                        {/* User limit (venue plans only) */}
                                        {!isDirectory && (
                                            <div className="flex items-center text-gray-400 mt-1">
                                                <UsersIcon className="h-4 w-4 mr-1" />
                                                <span className="text-sm">
                                                    {subscription.userLimit ? `Up to ${subscription.userLimit} active users` : 'Unlimited active users'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Lifetime pricing badge (directory plans) */}
                                        {subscription.isLifetimePricing && (
                                            <div className="flex items-center gap-1 mt-1 text-amber-400 text-xs font-semibold">
                                                <LockClosedIcon className="h-3.5 w-3.5" />
                                                Lifetime pricing — this rate is locked in forever
                                            </div>
                                        )}
                                    </div>

                                    {/* Price */}
                                    <div className="text-right ml-4 flex-shrink-0">
                                        {hasDiscount ? (
                                            <>
                                                <div className="text-sm text-gray-500 line-through">£{originalPrice}</div>
                                                <div className={`text-2xl font-bold ${isFounder ? 'text-amber-400' : 'text-emerald-400'}`}>
                                                    £{discountedPrice}
                                                </div>
                                            </>
                                        ) : (
                                            <div className={`text-2xl font-bold ${isFounder ? 'text-amber-400' : 'text-white'}`}>
                                                £{originalPrice}
                                            </div>
                                        )}
                                        <div className="text-sm text-gray-400">per month</div>
                                        {subscription.isLifetimePricing && (
                                            <div className="text-xs text-amber-400/80 mt-0.5">never changes</div>
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="space-y-1.5">
                                    {subscription.features.map((feature, index) => (
                                        <div key={index} className="flex items-center text-sm">
                                            <CheckIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${isFounder ? 'text-amber-400' : 'text-emerald-400'}`} />
                                            <span className="text-gray-300">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Founder note */}
                                {isFounder && subscription.founderNote && (
                                    <p className="mt-3 text-amber-300/70 text-xs italic">{subscription.founderNote}</p>
                                )}
                            </div>

                            {/* Badges */}
                            {hasDiscount && voucherLabel && (
                                <div className="absolute -top-2 right-4">
                                    <span className="bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center">
                                        <TicketIcon className="h-3 w-3 mr-1" />
                                        {voucherLabel}
                                    </span>
                                </div>
                            )}
                            {isFounder && (
                                <div className="absolute -top-2 left-4">
                                    <span className="bg-amber-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                                        ⭐ FOUNDER RATE
                                    </span>
                                </div>
                            )}
                            {subscription.id === 'medium' && !hasDiscount && !isFounder && (
                                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                                        Most Popular
                                    </span>
                                </div>
                            )}
                        </label>
                    );
                })}
            </div>

            {/* Voucher Code Input — venue plans only */}
            {!isPtDirectoryMode && validateVoucher && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-3 flex items-center">
                        <TicketIcon className="h-5 w-5 mr-2 text-emerald-400" />
                        Have a Voucher Code?
                    </h4>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={voucherInput}
                            onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                            placeholder="Enter voucher code"
                            className="flex-1 input-field"
                            disabled={voucherValid}
                        />
                        <button
                            type="button"
                            onClick={handleApplyVoucher}
                            disabled={!voucherInput || voucherValid}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                voucherValid
                                    ? 'bg-emerald-500 text-white cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            }`}
                        >
                            {voucherValid ? 'Applied ✓' : 'Apply'}
                        </button>
                    </div>
                    {voucherValid && (
                        <p className="text-emerald-400 text-sm mt-2 flex items-center">
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Voucher "{voucherCode}" applied — {voucherLabel} discount!
                        </p>
                    )}
                </div>
            )}

            {/* Directory note about no vouchers */}
            {isPtDirectoryMode && (
                <div className="bg-amber-900/10 border border-amber-700/40 rounded-lg p-4 text-sm text-amber-300/80">
                    <LockClosedIcon className="h-4 w-4 inline mr-1.5 mb-0.5" />
                    Directory plans already have <strong>lifetime pricing</strong> — your rate is locked in forever and vouchers don't apply.
                </div>
            )}

            {/* What's included — venue plans only */}
            {!isPtDirectoryMode && (
                <>
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                        <h4 className="text-white font-medium mb-2">What's included in all venue plans:</h4>
                        <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-300">
                            {[
                                'Class and session management',
                                'User booking system',
                                'Payment processing via GoCardless',
                                'Analytics and reporting',
                                'Mobile-responsive interface',
                                'Free PT Directory listing',
                                'Regular platform updates'
                            ].map(item => (
                                <div key={item} className="flex items-center">
                                    <CheckIcon className="h-4 w-4 text-emerald-400 mr-2 flex-shrink-0" />
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-400 mt-0.5">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-4 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="text-blue-400 font-medium text-sm">Billing Information</h4>
                                <p className="text-blue-300 text-sm mt-1">
                                    All plans are billed monthly in advance. You can upgrade, downgrade, or cancel your subscription 
                                    at any time from your venue dashboard. No long-term contracts required.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default VenueSubscriptionStep;
